type Vehicle = {
  center_bore: number;
  year: number;
  from: number;
  id: string;
  manufacturer: string;
  model: string;
  hp: number;
  kw: number;
  pcd: string;
  to: null;
  tyres: {
    [key: string]: boolean;
  };
  wheels: {
    [key: string]: {
      [key: string]: boolean;
    }
  };
};

type LicenseData = {
  license: string;
  country: string;
  data: Vehicle;
}

type State = {
  license: string | null,
  country: string | null,
  data: Vehicle | null,
};

export default class TyreSelectorByLicense {
  dom: {
    root: HTMLElement, // This module's root element
    form: HTMLFormElement,
    formSubmitBtn: HTMLButtonElement,
    license: HTMLInputElement,
    country: HTMLInputElement,
    errorMessage: HTMLDivElement,
  };

  parent;

  // Tyre selector filters state
  #state: State = {
    license: null,
    country: null,
    data: null,
  };

  #storageKey = 'ts__license'; // This stores form state
  #licenseStorageKeyPrefix = 'license'; // This stores all requested license data
  #submitting = false;

  /**
   * Tyreselector by license module
   *
   * @param element - Root element for this module
   * @param parent - Parent tyreselector instance
   */
  constructor(element: HTMLElement, parent) {
    this.parent = parent;
    this.dom = {} as any;
    this.dom.root = element;
    this.dom.form = element.querySelector('form[name="licenseSearchForm"]') as HTMLFormElement;
    this.dom.formSubmitBtn = this.dom.form?.querySelector('button[type="submit"]') as HTMLButtonElement;
    this.dom.license = this.dom.form?.querySelector('input[name="license"]') as HTMLInputElement;
    this.dom.country = this.dom.form?.querySelector('input[name="country"]') as HTMLInputElement;
    this.dom.errorMessage = this.dom.form?.querySelector('.ts__error') as HTMLDivElement;

    if(Object.keys(this.dom).every(key => this.dom[key])) {
      // Necessary dom elements found
      console.log('[TyreSelectorByLicense]: init license module');
      this.#initState();
      this.#attachEventListeners();
    } else {
      console.error('[TyreSelectorByLicense]: missing data or dom elements');
    }
  }

  /**
   * Initialises state from dom select values
   */
  #initState() {
    // Default state
    const initialState = this.getState();

    // Check values from input elements (values set by server)
    Object.keys(this.#state).forEach(key => {
      if(this.dom[key]) {
        const value = this.dom[key].value;
        if(value) {
          initialState[key] = value;
        }
      }
    });

    // Check storage for user's previous values
    const storageValues = sessionStorage.getItem(this.#storageKey);
    if(storageValues) {
      try {
        const storageState = JSON.parse(storageValues);
        Object.keys(storageState).forEach(key => {
          if(this.dom[key]) {
            initialState[key] = storageState[key];
          }
        });
      } catch(e) {
        console.error('[TyreSelectorBySize]: Failed to parse storage values', e);
      }
    }

    this.update(initialState);
    this.parent.initFilterbarSizes();

    // Enable submit if license is already filled
    if(this.dom.license.value.length >= 2) {
      this.dom.formSubmitBtn.disabled = false;
    }
  }

  /**
   * Attach event listeners to relevant dom elements
   */
  #attachEventListeners() {
    this.dom.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.dom.form.addEventListener('reset', this.onFormReset.bind(this));
    this.dom.license.addEventListener('keyup', this.onInputChange.bind(this));
    this.dom.license.addEventListener('blur', this.onInputChange.bind(this));
  }

  /**
   * Returns current filter values
   */
  getState() {
    return {...this.#state};
  }

  /**
   * Returns license data from session storage.
   */
  getStoredLicenseData(license: string, country: string): boolean | LicenseData {
    if(!license || !country) {
      return false;
    }

    // Check if license data is already stored
    const licenseKey = `${this.#licenseStorageKeyPrefix}__${country}_${license.replace(/\W/g, '_')}`;
    const licenseData = sessionStorage.getItem(licenseKey);
    if(licenseData) {
      try {
        const parsedData = JSON.parse(licenseData);
        if(parsedData.data) {
          return parsedData as LicenseData;
        }
      } catch(e) {
        console.error('[TyreSelectorByLicense]: Failed to parse license data from storage', e);
      }
    }

    return false;
  }

  /**
   * Stores license data to session storage
   */
  storeLicenseData(data: LicenseData) {
    if(typeof data !== 'object') {
      return;
    }

    const licenseKey = `${this.#licenseStorageKeyPrefix}__${data.country}_${data.license.replace(/\W/g, '_')}`;
    sessionStorage.setItem(licenseKey, JSON.stringify(data));
  }

  /**
   * Fetches vehicle data from server by license plate and country
   */
  fetchLicenseData(license: string, country: string): Promise<any> {
    if(!license || !country) {
      return Promise.reject('License or country not provided');
    }

    const params = new URLSearchParams({
      'stage-language': this.parent.selectedLang || '',
      tyreSelectorVersion: this.parent.version || '',
      license,
      country,
    }).toString();

    return fetch('/stage-ajax/searchLicense?' + params)
      .then(response => {
        if(!response.ok) {
          return Promise.reject(response.statusText);
        }
        return response;
      })
      .then(response => response.json())
      .then((data: boolean | string | LicenseData) => {
        console.log('[TyreSelectorByLicense]: fetched license data', data);

        // Check for when server returns 200 ok with "false" in body.
        if(data) {
          return data;
        } else {
          return Promise.reject('No data returned');
        }
      })
      .catch(error => {
        console.error('[TyreSelectorByLicense]: failed to fetch license data:', error);
        return Promise.reject(error);
      });
  }

  onFormSubmit(event: Event) {
    event.preventDefault();

    if(this.#submitting) {
      return;
    }

    this.#submitting = true;
    this.parent.setLoading(true);

    const license = this.dom.license.value.toUpperCase();
    const country = this.dom.country.value;
    const existingLicenseData = this.getStoredLicenseData(license, country);

    if(typeof existingLicenseData === 'object' && existingLicenseData?.license && existingLicenseData?.country && existingLicenseData?.data) {
      // Use existing data
      this.update({
        license: existingLicenseData.license,
        country: existingLicenseData.country,
        data: existingLicenseData.data,
      });
      sessionStorage.setItem(this.#storageKey, JSON.stringify(this.getState()));
      this.onLicenseDataReceived(existingLicenseData);
      this.#submitting = false;
    } else {
      // Fetch data from server
      this.fetchLicenseData(license, country)
        .then(data => {
          if(typeof data === 'string') {
            // A message from server
            this.showErrorMessage(data);
            this.parent.setLoading(false);
          } else if(typeof data === 'object' && data.data) {
            // Convert 'text' to 'license' for consistency
            const response = {
              ...data,
              license: data.text,
            };
            delete response.text;

            this.storeLicenseData(response);
            this.update(response);
            sessionStorage.setItem(this.#storageKey, JSON.stringify(this.getState()));
            this.onLicenseDataReceived(response);
          }
        })
        .catch(error => {
          const errorMsg = this.parent.loc.tyreselector_try_again_later || 'An error occurred. Please try again later. Sorry for the inconvenience.';
          this.showErrorMessage(errorMsg);
          this.parent.setLoading(false);
        })
        .finally(() => {
          this.#submitting = false;
        });
    }
  }

  /**
   * Perform action with license data
   * For embeds, this is redirect.
   * For standalone, this could be showing the results.
   */
  onLicenseDataReceived(licenseData: LicenseData) {
    console.log('[TyreSelectorByLicense]: onLicenseDataReceived', licenseData);

    // Get width, height, rim from license data
    // Car can have multiple fitting sizes
    const resultArray: any[] = [];
    if(licenseData?.data?.tyres) {
      for(const prop in licenseData.data.tyres) {
        if(Object.hasOwn(licenseData.data.tyres, prop)) {
          const splitSizeValues = this.splitTyreSizeIntoValues(prop);
          if(splitSizeValues) {
            resultArray.push({
              ...splitSizeValues,
              default: licenseData.data.tyres[prop],
            });
          }
        }
      }
    }

    const availableSizes: any[] = [];
    resultArray.forEach(size => {
      // Check availability by comparing to sizeData
      const matches = this.parent.sizeData.filter(item => {
        const [width, height, rim] = item;
        const match = width === size.width && height === size.height && rim === size.rim;
        return match;
      });

      if(matches.length) {
        availableSizes.push(size);
      }
    });

    if(availableSizes.length) {

      if(['embed', 'default'].includes(this.parent.type)) {
        // Redirect to size page
        let redirectSize = availableSizes[0];

        // If manufacturers default size exists, use it
        const defaultSize = availableSizes.find(size => size.default);
        if(defaultSize) {
          redirectSize = defaultSize;
        }

        const { width, height, rim } = redirectSize;

        // Store tyre data
        this.parent.storeTyreData({
          width: width,
          height: height,
          rim: rim,
          license: licenseData,
          manufacturer: licenseData.data.manufacturer,
          model: licenseData.data.model,
          year: licenseData.data.year,
          vehicle: licenseData.data,
          availableSizes
        }, 'license');

        this.parent.setVisibility('hidden');

        const analyticsEvent = {
          event: 'tyre_search_submit',
          tyre_search_type: 'License Search',
          tyre_size: `${width}/${height}R${rim}`,
          vehicle_manufacturer: licenseData.data.manufacturer,
          vehicle_year: (licenseData.data.year || 0).toString(),
          vehicle_model: licenseData.data.model,
        };

        this.parent.setView('license');

        this.parent.redirectToSizePage(width, height, rim, { analyticsEvent });
      } else {
        // TODO: Do something else with available sizes
        // If you stay on this page, set loading to false (it was set in form submit)
        this.parent.setLoading(false);
      }
    } else {
      // No sizes match
      this.parent.setLoading(false);
      const errorMsg = this.parent.loc.tyreselector_no_data || 'No data available for this model.';
      this.showErrorMessage(errorMsg);
    }
  }

  /**
   * This function takes a tyre size string and tries to extract width/height/rim infos into an object
   * Example: '255/55R18' -> { size: '255/55R18', width: 255, height: 55, rim: 18 }
   */
  splitTyreSizeIntoValues(tyreSize: string) {
    if(typeof tyreSize === 'undefined' || !tyreSize) {
      return null;
    }

    if((tyreSize.match(/\//g) || []).length !== 1) {
      return null; // only one '/' character
    }

    const hr = tyreSize.indexOf('/')!==-1 ? tyreSize.split('/')[1] : null;
    const hrLetters = hr ? hr.replace(/[0-9]/g,'') : null; // get all letters from hr string
    const splitter = hrLetters ? hrLetters.substring(0,1) : 'R'; // choose first letter to be the string splitting point
    const width = parseInt(tyreSize.split('/')[0].replace(/\D/g,'')); // remove all but numbers and convert to number
    const height = parseInt(tyreSize.split('/')[1].split(splitter)[0]); // characters after '/' up to splitter character define height (tyre's aspect ratio)
    const rim = parseInt(tyreSize.split(splitter)[1].replace(/\D/g,'')); // remove all but numbers and convert to number

    if(isNaN(width) || isNaN(height) || isNaN(rim)) {
      return null;
    }

    return {
      size: tyreSize,
      width,
      height,
      rim,
    };
  }

  showErrorMessage(message: string) {
    if(!message) {
      return;
    }

    this.dom.errorMessage.textContent = message;
    this.dom.errorMessage.removeAttribute('hidden');

    this.dom.license.setAttribute('aria-invalid', 'true');
    this.dom.license.setAttribute('aria-errormessage', this.dom.errorMessage.id);
  }

  hideErrorMessage() {
    this.dom.errorMessage.textContent = '';
    this.dom.errorMessage.setAttribute('hidden', 'hidden');

    this.dom.license.removeAttribute('aria-invalid');
    this.dom.license.removeAttribute('aria-errormessage');
  }

  /**
   * Clear form values and session storage
   */
  onFormReset(event: Event) {
    const values = this.getState();

    for (let key in values) {
      values[key] = null;
    }

    sessionStorage.removeItem(this.#storageKey);
    this.parent.setVisibility('visible');
    this.parent.setView(null);

    this.update(values);
    this.hideErrorMessage();
    this.dom.formSubmitBtn.disabled = true;

    // Clear tyreData
    this.parent.storeTyreData({}, 'license');
  }

  onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if(!value || value.length <= 2) {
      this.dom.formSubmitBtn.disabled = true;
    } else {
      this.dom.formSubmitBtn.disabled = false;
    }

    this.hideErrorMessage();
  }

  update(newState: State) {
    const oldState = this.getState();
    const futureState = { ...oldState, ...newState };

    this.#state = futureState;

    this.dom.license.value = futureState.license || '';
  }
}
